import React from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceTracker = ({ complianceData, title = "Compliance Milestone Tracking" }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10 border-success/20';
      case 'in-progress': return 'text-warning bg-warning/10 border-warning/20';
      case 'pending': return 'text-muted-foreground bg-muted border-border';
      case 'overdue': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'pending': return 'Circle';
      case 'overdue': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const calculateOverallProgress = () => {
    const completed = complianceData?.filter(item => item?.status === 'completed')?.length;
    return Math.round((completed / complianceData?.length) * 100);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">{calculateOverallProgress()}%</p>
            <p className="text-xs text-muted-foreground">Overall Progress</p>
          </div>
          <div className="w-12 h-12 relative">
            <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-muted stroke-current"
                strokeWidth="3"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-primary stroke-current"
                strokeWidth="3"
                strokeDasharray={`${calculateOverallProgress()}, 100`}
                strokeLinecap="round"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {complianceData?.map((item, index) => (
          <div key={index} className="flex items-center space-x-4 p-4 bg-muted/30 rounded-lg">
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${getStatusColor(item?.status)}`}>
              <Icon name={getStatusIcon(item?.status)} size={16} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-medium text-foreground truncate">{item?.milestone}</h4>
                <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(item?.status)}`}>
                  {item?.status?.replace('-', ' ')?.toUpperCase()}
                </span>
              </div>
              
              <p className="text-xs text-muted-foreground mb-2">{item?.description}</p>
              
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-4">
                  <span className="text-muted-foreground">
                    Due: {new Date(item.dueDate)?.toLocaleDateString()}
                  </span>
                  {item?.completedDate && (
                    <span className="text-success">
                      Completed: {new Date(item.completedDate)?.toLocaleDateString()}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <Icon name="User" size={12} className="text-muted-foreground" />
                  <span className="text-muted-foreground">{item?.assignedTo}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-lg font-semibold text-success">
              {complianceData?.filter(item => item?.status === 'completed')?.length}
            </p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-warning">
              {complianceData?.filter(item => item?.status === 'in-progress')?.length}
            </p>
            <p className="text-xs text-muted-foreground">In Progress</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-muted-foreground">
              {complianceData?.filter(item => item?.status === 'pending')?.length}
            </p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-error">
              {complianceData?.filter(item => item?.status === 'overdue')?.length}
            </p>
            <p className="text-xs text-muted-foreground">Overdue</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceTracker;